import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, Container, Row, Col, Alert, Form, Badge, Spinner } from 'react-bootstrap';
import { FaUser, FaEnvelope, FaPhone, FaBell, FaPlus, FaStickyNote, FaTrash, FaEdit, FaCheckCircle, FaClock } from 'react-icons/fa';
import { apiBaseUrl } from '../config.js';

const FollowUps = () => {
  const [followUps, setFollowUps] = useState([]);
  const [followUpsLoading, setFollowUpsLoading] = useState(false);
  const [followUpsError, setFollowUpsError] = useState('');
  const [followUpsStats, setFollowUpsStats] = useState({ total: 0, pending: 0, completed: 0, overdue: 0 });
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [editingFollowUp, setEditingFollowUp] = useState(null);
  const [followUpForm, setFollowUpForm] = useState({
    clientName: '',
    clientPhone: '',
    clientEmail: '',
    reminderDate: '',
    reminderTime: '09:00',
    notes: ''
  });
  const [newNote, setNewNote] = useState('');

  useEffect(() => {
    fetchFollowUps();
  }, []);

  const fetchFollowUps = async () => {
    setFollowUpsLoading(true);
    setFollowUpsError('');
    try {
      const token = localStorage.getItem('userToken');
      const response = await axios.get(`${apiBaseUrl}/api/admin/follow-ups`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          limit: 100
        }
      });
      
      if (response.data.success) {
        setFollowUps(response.data.followUps);
        setFollowUpsStats(response.data.stats);
      } else {
        setFollowUpsError('Error al cargar seguimientos');
      }
    } catch (err) {
      console.error('Error fetching follow-ups:', err);
      setFollowUpsError('Error al cargar seguimientos. Por favor intente de nuevo.');
    } finally {
      setFollowUpsLoading(false);
    }
  };

  const handleCreateFollowUp = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('userToken');
      await axios.post(`${apiBaseUrl}/api/admin/follow-ups`, 
        followUpForm,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      setFollowUpForm({
        clientName: '',
        clientPhone: '',
        clientEmail: '',
        reminderDate: '',
        reminderTime: '09:00',
        notes: ''
      });
      setShowFollowUpModal(false);
      setEditingFollowUp(null);
      fetchFollowUps();
    } catch (err) {
      console.error('Error creating follow-up:', err);
      setFollowUpsError('Error al crear seguimiento');
    }
  };

  const handleUpdateFollowUp = async (e) => {
    e.preventDefault();
    if (!editingFollowUp) return;
    
    try {
      const token = localStorage.getItem('userToken');
      await axios.put(`${apiBaseUrl}/api/admin/follow-ups/${editingFollowUp._id}`, 
        followUpForm,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      setFollowUpForm({
        clientName: '',
        clientPhone: '',
        clientEmail: '',
        reminderDate: '',
        reminderTime: '09:00',
        notes: ''
      });
      setShowFollowUpModal(false);
      setEditingFollowUp(null);
      fetchFollowUps();
    } catch (err) {
      console.error('Error updating follow-up:', err);
      setFollowUpsError('Error al actualizar seguimiento');
    }
  };

  const handleDeleteFollowUp = async (id) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar este seguimiento?')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('userToken');
      await axios.delete(`${apiBaseUrl}/api/admin/follow-ups/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      fetchFollowUps();
    } catch (err) {
      console.error('Error deleting follow-up:', err);
      setFollowUpsError('Error al eliminar seguimiento');
    }
  };

  const handleAddNote = async (followUpId) => {
    if (!newNote.trim()) return;
    
    try {
      const token = localStorage.getItem('userToken');
      await axios.post(`${apiBaseUrl}/api/admin/follow-ups/${followUpId}/notes`, 
        { note: newNote },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      setNewNote('');
      fetchFollowUps();
    } catch (err) {
      console.error('Error adding note:', err);
      setFollowUpsError('Error al agregar nota');
    }
  };

  const handleMarkAsCompleted = async (id) => {
    try {
      const token = localStorage.getItem('userToken');
      await axios.put(`${apiBaseUrl}/api/admin/follow-ups/${id}`, 
        { status: 'completed' },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      setFollowUps(prev => prev.map(fu => 
        fu._id === id ? { ...fu, status: 'completed' } : fu
      ));
    } catch (err) {
      console.error('Error marking as completed:', err);
      setFollowUpsError('Error al marcar como completado');
    }
  };

  const openEditModal = (followUp) => {
    setEditingFollowUp(followUp);
    
    // Extract time from reminderDate if it exists
    let timeValue = '09:00';
    if (followUp.reminderDate) {
      const date = new Date(followUp.reminderDate);
      timeValue = `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
    } else if (followUp.reminderTime) {
      timeValue = followUp.reminderTime;
    }
    
    setFollowUpForm({
      clientName: followUp.clientName,
      clientPhone: followUp.clientPhone,
      clientEmail: followUp.clientEmail || '',
      reminderDate: followUp.reminderDate ? new Date(followUp.reminderDate).toISOString().split('T')[0] : '',
      reminderTime: timeValue,
      notes: ''
    });
    setShowFollowUpModal(true);
  };

  const openCreateModal = () => {
    setEditingFollowUp(null);
    setFollowUpForm({
      clientName: '',
      clientPhone: '',
      clientEmail: '',
      reminderDate: '',
      reminderTime: '09:00',
      notes: ''
    });
    setShowFollowUpModal(true);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Container fluid style={{ paddingTop: '100px', paddingBottom: '40px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px', flexWrap: 'wrap', gap: '20px' }}>
        <h1 style={{ marginBottom: 0, color: '#2c3e50' }}>Seguimientos de Clientes</h1>
        <button 
          onClick={openCreateModal}
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            border: 'none',
            padding: '12px 24px',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: 'bold',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'translateY(-2px)';
            e.target.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.6)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 4px 15px rgba(102, 126, 234, 0.4)';
          }}
        >
          <FaPlus /> Nuevo Seguimiento
        </button>
      </div>

      {/* Stats Cards */}
      <Row className="mb-4">
        <Col xs={12} md={3}>
          <div style={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
            color: 'white', 
            padding: '20px', 
            borderRadius: '10px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
            textAlign: 'center'
          }}>
            <p style={{ fontSize: '16px', margin: 0, opacity: 0.9 }}>Total</p>
            <p style={{ fontSize: '32px', fontWeight: 'bold', margin: '10px 0 0 0' }}>{followUpsStats.total}</p>
          </div>
        </Col>
        <Col xs={12} md={3}>
          <div style={{ 
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', 
            color: 'white', 
            padding: '20px', 
            borderRadius: '10px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
            textAlign: 'center'
          }}>
            <p style={{ fontSize: '16px', margin: 0, opacity: 0.9 }}>Pendientes</p>
            <p style={{ fontSize: '32px', fontWeight: 'bold', margin: '10px 0 0 0' }}>{followUpsStats.pending}</p>
          </div>
        </Col>
        <Col xs={12} md={3}>
          <div style={{ 
            background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', 
            color: 'white', 
            padding: '20px', 
            borderRadius: '10px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
            textAlign: 'center'
          }}>
            <p style={{ fontSize: '16px', margin: 0, opacity: 0.9 }}>Vencidos</p>
            <p style={{ fontSize: '32px', fontWeight: 'bold', margin: '10px 0 0 0' }}>{followUpsStats.overdue}</p>
          </div>
        </Col>
        <Col xs={12} md={3}>
          <div style={{ 
            background: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)', 
            color: 'white', 
            padding: '20px', 
            borderRadius: '10px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
            textAlign: 'center'
          }}>
            <p style={{ fontSize: '16px', margin: 0, opacity: 0.9 }}>Completados</p>
            <p style={{ fontSize: '32px', fontWeight: 'bold', margin: '10px 0 0 0' }}>{followUpsStats.completed}</p>
          </div>
        </Col>
      </Row>

      {followUpsError && (
        <Alert variant="danger" className="mt-3">
          {followUpsError}
        </Alert>
      )}

      {followUpsLoading ? (
        <div className="text-center my-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Cargando...</span>
          </Spinner>
        </div>
      ) : followUps.length > 0 ? (
        <div>
          {followUps.map((followUp) => {
            const isOverdue = followUp.reminderDate && new Date(followUp.reminderDate) < new Date() && followUp.status === 'pending';
            const isDueSoon = followUp.reminderDate && new Date(followUp.reminderDate) < new Date(Date.now() + 3 * 24 * 60 * 60 * 1000) && followUp.status === 'pending' && !isOverdue;
            
            return (
              <Card key={followUp._id} className="mb-3" style={{ 
                borderLeft: isOverdue ? '5px solid #dc3545' : isDueSoon ? '5px solid #ffc107' : followUp.status === 'completed' ? '5px solid #28a745' : '5px solid #667eea',
                boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
              }}>
                <Card.Body>
                  <Row>
                    <Col md={8}>
                      <h5 style={{ marginBottom: '15px', color: '#2c3e50' }}>
                        <FaUser className="me-2" style={{ color: '#667eea' }} />
                        {followUp.clientName}
                        {followUp.status === 'completed' && (
                          <Badge bg="success" className="ms-2">
                            <FaCheckCircle className="me-1" />
                            Completado
                          </Badge>
                        )}
                        {isOverdue && (
                          <Badge bg="danger" className="ms-2">
                            <FaClock className="me-1" />
                            Vencido
                          </Badge>
                        )}
                        {isDueSoon && (
                          <Badge bg="warning" className="ms-2">
                            <FaClock className="me-1" />
                            Próximo
                          </Badge>
                        )}
                      </h5>
                      
                      <div style={{ marginBottom: '10px' }}>
                        <FaPhone className="me-2" style={{ color: '#667eea' }} />
                        <a 
                          href={`https://wa.me/${followUp.clientPhone?.replace(/\D/g, '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: '#25D366', fontWeight: 'bold', textDecoration: 'none' }}
                        >
                          {followUp.clientPhone}
                        </a>
                      </div>
                      
                      {followUp.clientEmail && (
                        <div style={{ marginBottom: '10px' }}>
                          <FaEnvelope className="me-2" style={{ color: '#667eea' }} />
                          <a href={`mailto:${followUp.clientEmail}`} style={{ color: '#667eea', textDecoration: 'none' }}>
                            {followUp.clientEmail}
                          </a>
                        </div>
                      )}
                      
                      {followUp.reminderDate && (
                        <div style={{ marginBottom: '10px' }}>
                          <FaBell className="me-2" style={{ color: isOverdue ? '#dc3545' : isDueSoon ? '#ffc107' : '#667eea' }} />
                          <strong>Recordatorio:</strong> {formatDate(followUp.reminderDate)}
                          {followUp.reminderSent && (
                            <Badge bg="info" className="ms-2">Email enviado</Badge>
                          )}
                        </div>
                      )}

                      {/* Notes Section */}
                      {followUp.notes && followUp.notes.length > 0 && (
                        <div style={{ 
                          background: '#f8f9fa', 
                          padding: '15px', 
                          borderRadius: '8px', 
                          marginTop: '15px',
                          maxHeight: '200px',
                          overflowY: 'auto'
                        }}>
                          <h6 style={{ color: '#667eea', marginBottom: '10px' }}>
                            <FaStickyNote className="me-2" />
                            Notas ({followUp.notes.length})
                          </h6>
                          {followUp.notes.map((note, index) => (
                            <div key={index} style={{ 
                              marginBottom: '10px', 
                              paddingBottom: '10px', 
                              borderBottom: index < followUp.notes.length - 1 ? '1px solid #dee2e6' : 'none' 
                            }}>
                              <p style={{ margin: '5px 0', fontSize: '13px', color: '#6c757d' }}>
                                {new Date(note.createdAt).toLocaleString('es-AR', { 
                                  dateStyle: 'short', 
                                  timeStyle: 'short' 
                                })} - <strong>{note.createdBy}</strong>
                              </p>
                              <p style={{ margin: '5px 0', color: '#2c3e50' }}>{note.text}</p>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Add Note Form */}
                      {followUp.status === 'pending' && (
                        <div style={{ marginTop: '15px' }}>
                          <Form.Group>
                            <Form.Control
                              as="textarea"
                              rows={2}
                              placeholder="Agregar nota..."
                              value={newNote}
                              onChange={(e) => setNewNote(e.target.value)}
                              style={{ 
                                borderColor: '#667eea',
                                resize: 'none'
                              }}
                            />
                            <button
                              onClick={() => {
                                handleAddNote(followUp._id);
                              }}
                              disabled={!newNote.trim()}
                              style={{
                                marginTop: '10px',
                                background: newNote.trim() ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#ccc',
                                color: 'white',
                                border: 'none',
                                padding: '8px 16px',
                                borderRadius: '6px',
                                fontSize: '14px',
                                fontWeight: 'bold',
                                cursor: newNote.trim() ? 'pointer' : 'not-allowed',
                                transition: 'all 0.3s ease'
                              }}
                            >
                              <FaStickyNote className="me-1" /> Agregar Nota
                            </button>
                          </Form.Group>
                        </div>
                      )}
                    </Col>
                    
                    <Col md={4} className="text-end">
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {followUp.status === 'pending' && (
                          <>
                            <button
                              onClick={() => handleMarkAsCompleted(followUp._id)}
                              style={{
                                background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                                color: 'white',
                                border: 'none',
                                padding: '10px 20px',
                                borderRadius: '6px',
                                fontSize: '14px',
                                fontWeight: 'bold',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease'
                              }}
                            >
                              <FaCheckCircle className="me-1" /> Marcar Completado
                            </button>
                            <button
                              onClick={() => openEditModal(followUp)}
                              style={{
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                color: 'white',
                                border: 'none',
                                padding: '10px 20px',
                                borderRadius: '6px',
                                fontSize: '14px',
                                fontWeight: 'bold',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease'
                              }}
                            >
                              <FaEdit className="me-1" /> Editar
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => handleDeleteFollowUp(followUp._id)}
                          style={{
                            background: 'linear-gradient(135deg, #eb3349 0%, #f45c43 100%)',
                            color: 'white',
                            border: 'none',
                            padding: '10px 20px',
                            borderRadius: '6px',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease'
                          }}
                        >
                          <FaTrash className="me-1" /> Eliminar
                        </button>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            );
          })}
        </div>
      ) : (
        <Alert variant="info" className="text-center">
          <FaBell className="me-2" />
          No hay seguimientos registrados todavía.
        </Alert>
      )}

      {/* Follow-Up Modal */}
      {showFollowUpModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1050
          }}
          onClick={() => setShowFollowUpModal(false)}
        >
          <div 
            style={{
              background: 'white',
              borderRadius: '12px',
              padding: '30px',
              maxWidth: '600px',
              width: '90%',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginBottom: '25px', color: '#2c3e50' }}>
              {editingFollowUp ? 'Editar Seguimiento' : 'Nuevo Seguimiento'}
            </h3>
            
            <Form onSubmit={editingFollowUp ? handleUpdateFollowUp : handleCreateFollowUp}>
              <Form.Group className="mb-3">
                <Form.Label style={{ fontWeight: 'bold', color: '#667eea' }}>
                  <FaUser className="me-2" />
                  Nombre del Cliente *
                </Form.Label>
                <Form.Control
                  type="text"
                  required
                  value={followUpForm.clientName}
                  onChange={(e) => setFollowUpForm({ ...followUpForm, clientName: e.target.value })}
                  placeholder="Ej: Juan Pérez"
                  style={{ borderColor: '#667eea' }}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label style={{ fontWeight: 'bold', color: '#667eea' }}>
                  <FaPhone className="me-2" />
                  Teléfono *
                </Form.Label>
                <Form.Control
                  type="text"
                  required
                  value={followUpForm.clientPhone}
                  onChange={(e) => setFollowUpForm({ ...followUpForm, clientPhone: e.target.value })}
                  placeholder="Ej: +54 11 1234-5678"
                  style={{ borderColor: '#667eea' }}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label style={{ fontWeight: 'bold', color: '#667eea' }}>
                  <FaEnvelope className="me-2" />
                  Email
                </Form.Label>
                <Form.Control
                  type="email"
                  value={followUpForm.clientEmail}
                  onChange={(e) => setFollowUpForm({ ...followUpForm, clientEmail: e.target.value })}
                  placeholder="Ej: juan@email.com"
                  style={{ borderColor: '#667eea' }}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label style={{ fontWeight: 'bold', color: '#667eea' }}>
                  <FaBell className="me-2" />
                  Fecha de Recordatorio
                </Form.Label>
                <Form.Control
                  type="date"
                  value={followUpForm.reminderDate}
                  onChange={(e) => setFollowUpForm({ ...followUpForm, reminderDate: e.target.value })}
                  style={{ borderColor: '#667eea' }}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label style={{ fontWeight: 'bold', color: '#667eea' }}>
                  🕐 Hora del Recordatorio
                </Form.Label>
                <Form.Control
                  type="time"
                  value={followUpForm.reminderTime}
                  onChange={(e) => setFollowUpForm({ ...followUpForm, reminderTime: e.target.value })}
                  style={{ borderColor: '#667eea' }}
                />
                <Form.Text className="text-muted">
                  Se enviará un email a info@tasacionesya.com en la fecha y hora seleccionada
                </Form.Text>
              </Form.Group>

              {!editingFollowUp && (
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: 'bold', color: '#667eea' }}>
                    <FaStickyNote className="me-2" />
                    Nota Inicial
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={followUpForm.notes}
                    onChange={(e) => setFollowUpForm({ ...followUpForm, notes: e.target.value })}
                    placeholder="Agregar una nota sobre este seguimiento..."
                    style={{ borderColor: '#667eea', resize: 'none' }}
                  />
                </Form.Group>
              )}

              <div style={{ display: 'flex', gap: '10px', marginTop: '25px' }}>
                <button
                  type="submit"
                  style={{
                    flex: 1,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                >
                  {editingFollowUp ? 'Actualizar' : 'Crear Seguimiento'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowFollowUpModal(false)}
                  style={{
                    flex: 1,
                    background: '#6c757d',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                >
                  Cancelar
                </button>
              </div>
            </Form>
          </div>
        </div>
      )}
    </Container>
  );
};

export default FollowUps;
